import { createSignal, onMount, onCleanup, Switch, Match, For } from 'solid-js';
import {
  EventType,
  NextBestAction,
  SearchIntent,
  SearchIntentAction,
} from '@/types/constants';
import { ActionCTA } from '../Actions/ActionCTA';
import { eventTrackerStore } from '@/utils/event-tracker-store';
import styles from '@brainfish-ai/solid-components/styles?inline';
import { ActionButtons } from '@brainfish-ai/solid-components';
import classNames from 'classnames';
import { ActionsSkeleton } from './ActionsSkeleton';

type NextBestActionsProps = {
  actions: NextBestAction[];
  apiHost: string;
  apiKey: string;
  searchQueryId?: string;
  searchQuery?: string;
  promptId?: string;
  answer: string;
  hideAnswer?: boolean;
  className?: string;
  searchIntent?: SearchIntent;
  onAskNewQuestion?: () => void;
  loading?: boolean;
};

export const NextBestActions = (props: NextBestActionsProps) => {
  const [actions] = createSignal(props.actions);
  const [eventTracker] = eventTrackerStore;
  let actionButtonsRef!: HTMLDivElement | undefined;

  const trackIntentAction = async (action: SearchIntentAction) => {
    await eventTracker?.track({
      name: action.action.options.title,
      type: EventType.IntentActionClicked,
      data: {
        searchQueryId: props.searchQueryId,
        searchQuery: props.searchQuery,
        promptId: props.promptId,
        answer: props.answer,
        intentActionClicked: {
          id: action.id,
          type: action.type,
        },
      },
    });
  };

  // handle BrainfishActionClick event
  const handleActionClick = (event: Event) => {
    const action = (event as CustomEvent).detail?.action as SearchIntentAction;
    // track intent action if available
    if (action) {
      trackIntentAction(action);
    }
  };

  const handleAskNewQuestionClick = async () => {
    props.onAskNewQuestion?.();
    await eventTracker?.track({
      name: 'Ask New Question',
      type: EventType.AskNewQuestion,
      data: {},
    });
  };

  onMount(() => {
    if (actionButtonsRef) {
      actionButtonsRef.addEventListener(
        'BrainfishActionClick',
        handleActionClick,
      );
    }
  });
  onCleanup(() => {
    if (actionButtonsRef) {
      actionButtonsRef.removeEventListener(
        'BrainfishActionClick',
        handleActionClick,
      );
    }
  });

  const trackAction = async (action: NextBestAction) => {
    await eventTracker?.track({
      name: action.label,
      type: EventType.SearchWidgetAction,
      data: {
        searchQueryId: props.searchQueryId,
        searchQuery: props.searchQuery,
        promptId: props.promptId,
        answer: props.answer,
      },
    });
  };

  return (
    <div data-testid="NextBestAction" ref={actionButtonsRef}>
      <Switch>
        <Match when={props.loading}>
          <ActionsSkeleton />
        </Match>
        {/* Intent Actions  */}
        <Match
          when={props.searchIntent && props.searchIntent.actions.length > 0}
        >
          <div class={classNames('mt-4', props.className)}>
            <style>{`${styles}`}</style>
            <ActionButtons
              actions={props.searchIntent?.actions as any}
              className={`action-buttons`}
              buttonClasses="flex flex-row w-full border bg-gray-50 !border-[var(--bfsearch-border-color)] !rounded-md p-2 !text-sm hover:!bg-gray-100 hover:!text-black"
              onAskNewQuestionClick={handleAskNewQuestionClick}
            />
          </div>
        </Match>
        {/* Default Next Best Actions */}
        <Match when={actions().length > 0}>
          <div
            class={classNames(
              props.className,
              'flex flex-col gap-4 my-4',
              props.hideAnswer ? 'w-full' : 'items-center justify-end mb-0',
            )}
          >
            <For each={actions()}>
              {(action) => (
                <ActionCTA
                  action={action}
                  onClick={() => trackAction(action)}
                  searchQuery={props.searchQuery || ''}
                  searchAnswer={props.answer}
                  center={true}
                />
              )}
            </For>
          </div>
        </Match>
      </Switch>
    </div>
  );
};
